import { useCallback, useEffect, useRef, useState } from 'react';

import { confirmMemberDocumentAvailable, fetchDocumentUrl } from 'modules/Documents/api/documents';
import openDocument from 'modules/Documents/openDocument';
import useCompleteMemberTask from 'modules/Tasks/hooks/useCompleteMemberTask';
import { SubmitActionTask } from 'modules/Tasks/types';
import { sendDatadogRumError } from 'utils/analytics/datadogRum';

interface UseTaskDocumentDownloadParams {
    task: SubmitActionTask;
    documentUuid: string | undefined;
    fileName: string;
    open: boolean;
    onClose: () => void;
}

/**
 * Why a download attempt failed, which is what picks the modal's copy.
 *
 * - `download` — nothing was retrieved; retrying starts over.
 * - `unavailable` — something was retrieved but we will not claim the member has it: the export came
 *   back empty, or it is no longer in their document centre.
 * - `completion` — the file was delivered and only the status update failed; retrying finishes that.
 */
export type DownloadErrorReason = 'download' | 'unavailable' | 'completion';

/**
 * The export could not be vouched for, so the task must not be completed against it. Carries the
 * specific reason for Datadog; the member-facing copy is the same either way, because both mean the
 * same thing to them — we will not claim they have this file.
 */
class DocumentVerificationError extends Error {
    constructor(reason: string) {
        super(`Document could not be verified: ${reason}`);
        this.name = 'DocumentVerificationError';
    }
}

/** Extensions for the content types this export endpoint actually returns. */
const EXTENSION_BY_MIME_TYPE: Record<string, string> = {
    'application/zip': '.zip',
    'application/x-zip-compressed': '.zip',
    'application/pdf': '.pdf',
    'text/csv': '.csv',
};

/**
 * A name the OS can actually open. `link.download` is taken literally, so an extensionless fallback
 * saves a file nothing is associated with — recoverable only by renaming it by hand, and the task is
 * gone by then. The context's `file_name` is unvalidated backend data, so it can arrive empty.
 */
export function resolveDownloadFileName(fileName: string, blob: Blob): string {
    const trimmed = fileName.trim();
    if (trimmed) {
        return trimmed;
    }
    // `Blob.type` carries the full content type, parameters included — `text/csv;charset=utf-8` is a
    // CSV the exact lookup would miss, handing back an extensionless name for a type we do know.
    const mimeType = blob.type.split(';')[0].trim().toLowerCase();
    return `download${EXTENSION_BY_MIME_TYPE[mimeType] ?? ''}`;
}

/**
 * Owns the download flow for the download_document task modal: fetch a presigned URL for the member
 * document, stream it to a blob, check the member really has something, mark the task complete, then
 * hand the blob to the browser.
 *
 * **Completion only ever asserts what we can check.** The browser's save step is not observable on
 * any platform — `openDocument` dispatches a synthetic click and returns void — so completing can
 * never mean "this file is on your disk". What it does mean is that non-empty bytes were retrieved
 * and that the export is still reachable in the member's own document centre, which is verified
 * immediately before the status update. That second check is what makes completing honest: a save
 * that silently fails is not a dead end, because a confirmed second copy is one click away in
 * Documents.
 *
 * The save runs *after* the status update, not before. WebKit begins a blob-URL save by navigating
 * to it, which aborts every request already in flight on the page — saving first killed the
 * completion POST outright (status 0 after ~5ms) on every Safari and iOS download.
 *
 * The cost of that ordering: delivery hangs off react-query's mutate-scoped callbacks, which are
 * skipped entirely if the provider unmounts before the POST settles. A member who navigates away
 * inside that window completes the task and gets no file. The document-centre check above is what
 * keeps that recoverable rather than terminal — the export is still one click away in Documents.
 */
export default function useTaskDocumentDownload({
    task,
    documentUuid,
    fileName,
    open,
    onClose,
}: UseTaskDocumentDownloadParams) {
    const [isDownloading, setIsDownloading] = useState(false);
    const [errorReason, setErrorReason] = useState<DownloadErrorReason | null>(null);
    // Set once the bytes reach the browser. Survives a completion failure so the retry finishes the
    // task instead of downloading a second copy of a file the member already has.
    const hasDownloadedRef = useRef(false);
    // Holds the verified bytes between the status update being sent and it settling, so nothing
    // reaches the browser while the completion POST is still in flight. See the note above on WebKit.
    const pendingBlobRef = useRef<Blob | null>(null);
    // Bumped on every open/close transition, retiring whatever is still in flight. The reset below
    // clears the visible state, so without a generation to compare against, a request that settles
    // after the member closed the modal writes into whatever session is open by then.
    const attemptRef = useRef(0);

    const completeMemberTask = useCompleteMemberTask(task, onClose);

    // Reset on every open/close transition, not just close: a request that rejects after the member
    // has closed the modal still lands here, and without the reset-on-open that stale error greets
    // them the next time they open a perfectly downloadable task. Repointing the modal at a
    // different document retires the in-flight request just as much as closing it does — otherwise
    // that request saves the previous document and marks this one downloaded.
    useEffect(() => {
        attemptRef.current += 1;
        setIsDownloading(false);
        setErrorReason(null);
    }, [open, documentUuid]);

    // Keyed to the document rather than the open/close transition: a file already on disk stays on
    // disk once the modal closes. The completion-failure copy tells the member to try again, and
    // closing the modal and reopening it is a natural way to do that — resetting here would make
    // that retry save a second copy of a file they already have, which is what the ref exists to
    // prevent.
    useEffect(() => {
        hasDownloadedRef.current = false;
        pendingBlobRef.current = null;
    }, [documentUuid]);

    /** Hands the bytes to the browser. Idempotent, so both completion outcomes can call it. */
    const saveDownloadedFile = useCallback(() => {
        const blob = pendingBlobRef.current;
        if (!blob) {
            return;
        }
        pendingBlobRef.current = null;
        openDocument(blob, resolveDownloadFileName(fileName, blob), true);
        hasDownloadedRef.current = true;
    }, [fileName]);

    const completeTask = useCallback(
        (attempt: number) => {
            completeMemberTask({
                trackingEvent: 'Document Download Completed',
                trackingProperties: { document_uuid: documentUuid },
                onSuccess: () => {
                    if (attemptRef.current !== attempt) {
                        // Reopening the modal on the same document retires this attempt without
                        // clearing the blob, so without this the file would drop into a session that
                        // never asked for it and latch as downloaded there, leaving the next click
                        // unable to fetch it for real. The task is completed either way, and the
                        // document-centre copy this completion was gated on is still one click away.
                        pendingBlobRef.current = null;
                        return;
                    }
                    saveDownloadedFile();
                },
                onError: () => {
                    if (attemptRef.current !== attempt) {
                        // The member closed the modal while this was in flight. Saving now would drop
                        // a file on a session that walked away and latch it as downloaded, so the
                        // reopened modal could no longer fetch it for real.
                        return;
                    }
                    // The bytes are verified and in hand; deliver them whether or not the task could
                    // be closed out, so the retry has only the status update left to redo.
                    saveDownloadedFile();
                    setIsDownloading(false);
                    setErrorReason('completion');
                },
            });
        },
        [completeMemberTask, documentUuid, saveDownloadedFile]
    );

    const handleDownload = useCallback(async () => {
        if (!documentUuid) {
            return;
        }
        const attempt = attemptRef.current;
        setIsDownloading(true);
        setErrorReason(null);

        // The bytes are already on disk and only the status update failed — retry that alone. Not
        // re-verified: the member demonstrably has this file, which is a stronger guarantee than the
        // document-centre check stands in for, and refusing here would strand a non-dismissable task.
        if (hasDownloadedRef.current) {
            completeTask(attempt);
            return;
        }

        try {
            const url = await fetchDocumentUrl(documentUuid);
            const response = await fetch(url);
            // fetch() resolves for 4xx/5xx. An expired or forbidden presigned URL would otherwise be
            // turned into an error-body blob and mark the task complete with nothing downloaded — and
            // the task is non-dismissable, so the member would be left with no way to retry.
            if (!response.ok) {
                throw new Error(`Document download failed with status ${response.status}`);
            }
            const blob = await response.blob();
            // The member closed the modal while this was in flight. Saving a file they walked away
            // from and completing the task under a later session is worse than dropping the attempt:
            // the task stays open, so the reopened modal can download it again for real.
            if (attemptRef.current !== attempt) {
                return;
            }

            // A 200 with no body is the one delivery failure that is actually detectable here, and
            // saving a zero-byte export is worse than not saving at all.
            if (blob.size === 0) {
                throw new DocumentVerificationError('the export came back empty');
            }
            // Re-checked now rather than inferred from the presigned URL fetched seconds ago: an
            // export deleted mid-flow cancels this very task, so completing against one would close
            // out a task for a document the member can no longer reach.
            if (!(await confirmMemberDocumentAvailable(documentUuid))) {
                throw new DocumentVerificationError('it is no longer in the member document centre');
            }
            if (attemptRef.current !== attempt) {
                return;
            }

            pendingBlobRef.current = blob;
            completeTask(attempt);
        } catch (error) {
            // Logged whether or not anyone is still watching — the request did fail.
            sendDatadogRumError('Task document download failed', error as Error, {
                taskId: task.id,
                document_uuid: documentUuid,
            });
            if (attemptRef.current !== attempt) {
                return;
            }
            setIsDownloading(false);
            setErrorReason(error instanceof DocumentVerificationError ? 'unavailable' : 'download');
        }
    }, [documentUuid, completeTask, task.id]);

    return {
        isDownloading,
        errorReason,
        handleDownload,
    };
}
